import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircleIcon, ArrowRightIcon, UserCheckIcon, PlaneIcon, ShieldCheckIcon, ClockIcon, MapPinIcon, CheckCircle2Icon, ChevronDownIcon, StarIcon } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { useLanguage } from '../hooks/useLanguage';
export function IbizaAirportTransfer() {
  const {
    language
  } = useLanguage();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // SEO meta tags
  useEffect(() => {
    document.title = language === 'en' ? 'Ibiza Airport Transfer | Fixed Prices via WhatsApp' : 'Transfer Aeropuerto Ibiza | Precios Fijos por WhatsApp';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descContent = language === 'en' ? 'Book your Ibiza airport transfer in minutes. Fixed prices, local drivers, flight tracking, instant WhatsApp quote and 24/7 service.' : 'Reserva tu transfer en el aeropuerto de Ibiza en minutos. Precios fijos, conductores locales, seguimiento de vuelo, presupuesto instantáneo por WhatsApp y servicio 24/7.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [language]);
  // WhatsApp URL
  const whatsappUrl = language === 'en' ? 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20an%20airport%20transfer%20in%20Ibiza.' : 'https://wa.me/34613756211?text=Hola%2C%20necesito%20un%20traslado%20desde%20el%20aeropuerto%20en%20Ibiza.';
  // Routes Data
  const routes = [{
    name: 'Ibiza Airport → Ibiza Town',
    prices: {
      standard: '120€',
      premium: '100€',
      group: '120€'
    }
  }, {
    name: "Ibiza Airport → Playa d'en Bossa",
    prices: {
      standard: '100€',
      premium: '100€',
      group: '120€'
    }
  }, {
    name: 'Ibiza Airport → San Antonio',
    prices: {
      standard: '120€',
      premium: '120€',
      group: '140€'
    }
  }, {
    name: 'Ibiza Airport → Santa Eulalia',
    prices: {
      standard: '140€',
      premium: '140€',
      group: '160€'
    }
  }, {
    name: 'Ibiza Airport → San José',
    prices: {
      standard: '120€',
      premium: '120€',
      group: '140€'
    }
  }];
  // FAQs Data
  const faqs = [{
    q: 'How much is an Ibiza airport transfer?',
    a: 'Airport transfers usually start from 100€ depending on destination, vehicle and number of passengers. We always confirm the fixed price before pickup.'
  }, {
    q: 'Do you track delayed flights?',
    a: 'Yes. We monitor your flight and adjust the pickup time automatically.'
  }, {
    q: 'Can I book a return airport transfer too?',
    a: 'Yes. Arrival and departure transfers can be booked together.'
  }, {
    q: 'Do you provide child seats?',
    a: 'Yes, on request.'
  }, {
    q: 'Will the driver wait for us at arrivals?',
    a: 'Yes. We offer meet & greet at the airport.'
  }, {
    q: 'Do you cover villas and private addresses from the airport?',
    a: 'Yes. We provide airport transfers to hotels, villas, private residences and other destinations across Ibiza.'
  }];
  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <main className="flex-grow pt-20" data-id="element-347">
      {/* 1. Hero */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] flex items-center bg-navy overflow-hidden" data-id="element-348">
        <div className="absolute inset-0 z-0" data-id="element-349">
          <img src="https://cdn.magicpatterns.com/uploads/bUsL83xf6r5jQrvHQbyhWN/pick_up_transfer_airport.jpg" alt="Premium Mercedes V-Class airport pickup at Ibiza Airport with professional driver greeting passengers" className="w-full h-full object-cover object-[60%_center] md:object-center opacity-60" data-id="element-350" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-navy/30" data-id="element-351" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 w-full py-12 md:py-20" data-id="element-352">
          <div className="max-w-2xl" data-id="element-353">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-354">
              <div className="inline-flex items-center space-x-1.5 md:space-x-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-5 md:mb-8 border border-white/15" data-id="element-355">
                <span className="text-gold-subtle text-xs md:text-sm" data-id="element-356">★</span>
                <span className="text-white text-[10px] md:text-xs font-semibold tracking-wide" data-id="element-357">
                  4.9★ rated · Flight tracking · 24/7 · Fixed price
                </span>
              </div>
              <h1 className="text-[1.75rem] md:text-6xl font-heading text-white mb-5 md:mb-8 leading-[1.15] md:leading-[1.1]" data-id="element-358">
                Ibiza Airport Transfer — Fixed Price via WhatsApp
              </h1>
              <p className="text-base md:text-xl text-white/90 mb-8 md:mb-12 leading-relaxed max-w-md md:max-w-xl" data-id="element-359">
                Private airport pickups across Ibiza with fixed prices, local
                drivers and fast WhatsApp quotes. No upfront payment required.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4" data-id="element-360">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-361">
                  <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-362" />
                  <span data-id="element-363">Get airport transfer price on WhatsApp</span>
                </a>
                <a href="#routes" className="w-full sm:w-auto flex items-center justify-center space-x-2 text-white hover:text-gold-subtle px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors" data-id="element-364">
                  <span data-id="element-365">See popular routes</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-366" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <section className="bg-warm-cream border-y border-stone/10 py-10 md:py-14" data-id="element-367">
        <div className="mx-auto max-w-7xl px-5" data-id="element-368">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8" data-id="element-369">
            {[{
            text: 'Meet & greet at arrivals',
            icon: UserCheckIcon
          }, {
            text: 'Flight tracking included',
            icon: PlaneIcon
          }, {
            text: 'Fixed prices, no surprises',
            icon: ShieldCheckIcon
          }, {
            text: 'Local drivers',
            icon: MapPinIcon
          }, {
            text: 'Reply in under 2 minutes',
            icon: ClockIcon
          }].map((item, index) => <div key={index} className="flex flex-col items-center text-center space-y-3" data-id="element-370">
                <div className="w-12 h-12 rounded-full bg-warm-white flex items-center justify-center" data-id="element-371">
                  <item.icon className="w-5 h-5 text-gold-subtle" data-id="element-372" />
                </div>
                <span className="text-xs md:text-sm font-medium text-slate leading-tight" data-id="element-373">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. Problem / Solution */}
      <section className="py-16 md:py-24 bg-white" data-id="element-374">
        <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-375">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-376">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-8" data-id="element-377">
              Avoid delays, unclear prices and airport pickup stress
            </h2>
            <div className="space-y-6 text-lg text-slate leading-relaxed" data-id="element-378">
              <p data-id="element-379">
                Many airport transfers fail for the same reasons: delayed
                pickups, unclear pricing, poor communication and drivers who
                don't really know the island.
              </p>
              <p data-id="element-380">
                We make airport pickups simple. We track your flight, meet you
                on arrival, help with luggage and confirm the fixed price before
                your journey starts.
              </p>
              <p className="font-medium text-navy pt-4" data-id="element-381">
                No stress, no surprises, no waiting around after landing.
              </p>
            </div>

            {/* CTA after problem/solution */}
            <div className="mt-10" data-id="element-382">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-383">
                <WhatsAppIcon className="w-5 h-5" data-id="element-384" />
                <span data-id="element-385">Get exact airport transfer price on WhatsApp</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. Popular Routes + Pricing */}
      <section id="routes" className="py-16 md:py-24 bg-warm-white border-y border-stone/10" data-id="element-386">
        <div className="mx-auto max-w-7xl px-5" data-id="element-387">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-388">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-389">
              Popular Ibiza airport transfer routes
            </h2>
            <p className="text-slate leading-relaxed" data-id="element-390">
              These are some of our most requested airport transfer routes. If
              your destination is not listed, send us your route on WhatsApp and
              we'll confirm your fixed price in under 2 minutes.
            </p>
          </div>

          <div className="space-y-6 mb-12" data-id="element-391">
            {routes.map((route, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white border border-stone/10 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-392">
                <div className="flex items-center space-x-2 mb-6" data-id="element-393">
                  <MapPinIcon className="w-5 h-5 text-slate" data-id="element-394" />
                  <h3 className="text-xl md:text-2xl font-heading text-navy" data-id="element-395">
                    {route.name}
                  </h3>
                </div>

                <div className="grid grid-cols-3 gap-px bg-stone/10 rounded-xl overflow-hidden" data-id="element-396">
                  {/* Standard */}
                  <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-397">
                    <div className="h-5 md:h-6 mb-1" data-id="element-398" />
                    <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-399">
                      STANDARD
                    </div>
                    <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-400">
                      from {route.prices.standard}
                    </div>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-401">
                      <span data-id="element-402">Get price</span>
                      <ArrowRightIcon className="w-3 h-3" data-id="element-403" />
                    </a>
                  </div>

                  {/* Premium Van */}
                  <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-404">
                    <div className="h-5 md:h-6 mb-1 flex items-center justify-center" data-id="element-405">
                      <span className="inline-flex items-center space-x-0.5 bg-gold-subtle/15 text-gold-subtle text-[6px] md:text-[9px] font-bold tracking-wider px-1.5 md:px-2.5 py-0.5 rounded-full whitespace-nowrap border border-gold-subtle/20" data-id="element-406">
                        <span data-id="element-407">⭐</span>
                        <span data-id="element-408">MOST POPULAR</span>
                      </span>
                    </div>
                    <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-409">
                      PREMIUM VAN
                    </div>
                    <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-410">
                      from {route.prices.premium}
                    </div>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-411">
                      <span data-id="element-412">Get price</span>
                      <ArrowRightIcon className="w-3 h-3" data-id="element-413" />
                    </a>
                  </div>

                  {/* Group Van */}
                  <div className="bg-white p-3 md:p-5 text-center flex flex-col items-center" data-id="element-414">
                    <div className="h-5 md:h-6 mb-1" data-id="element-415" />
                    <div className="text-[9px] md:text-xs font-medium tracking-wider text-stone uppercase mb-1.5 md:mb-2" data-id="element-416">
                      GROUP VAN
                    </div>
                    <div className="text-xl md:text-3xl font-heading text-navy mb-2 md:mb-3" data-id="element-417">
                      from {route.prices.group}
                    </div>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-[11px] md:text-xs text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-418">
                      <span data-id="element-419">Get price</span>
                      <ArrowRightIcon className="w-3 h-3" data-id="element-420" />
                    </a>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center" data-id="element-421">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-422">
              <WhatsAppIcon className="w-5 h-5" data-id="element-423" />
              <span data-id="element-424">Get airport transfer price on WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* 5. Areas We Cover */}
      <section className="py-16 md:py-24 bg-white" data-id="element-425">
        <div className="mx-auto max-w-4xl px-5 text-center" data-id="element-426">
          <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-427">
            Airport transfers across Ibiza
          </h2>
          <p className="text-slate leading-relaxed mb-4 max-w-2xl mx-auto" data-id="element-428">
            We provide airport transfers from Ibiza Airport to all major
            destinations on the island, including hotels, villas, marinas,
            restaurants and private residences.
          </p>
          <p className="text-slate leading-relaxed mb-6 max-w-2xl mx-auto" data-id="element-429">
            From Ibiza Airport to hotels, villas, marinas and private addresses
            across the island.
          </p>
          <p className="text-slate leading-relaxed mb-10 max-w-2xl mx-auto" data-id="element-430">
            Popular routes include Ibiza Town, Playa d'en Bossa, Talamanca,
            Marina Botafoch, Santa Eulalia, San Antonio and Portinatx.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-431">
            {['Ibiza Town', "Playa d'en Bossa", 'Talamanca', 'Marina Botafoch', 'Cap Martinet', 'San Antonio', 'Santa Eulalia', 'Santa Gertrudis', 'Es Cubells', 'Portinatx', 'Cala Jondal'].map((area, index) => <span key={index} className="px-4 py-2 bg-warm-white border border-stone/10 rounded-full text-sm font-medium text-navy" data-id="element-432">
                {area}
              </span>)}
          </div>

          <p className="text-slate font-medium mb-6" data-id="element-433">
            Need another route? Send us your pickup and drop-off on WhatsApp.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-434">
            <MessageCircleIcon className="w-4 h-4" data-id="element-435" />
            <span data-id="element-436">Send route on WhatsApp</span>
            <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-437" />
          </a>
        </div>
      </section>

      {/* 6. Why Choose Us — light background */}
      <section className="py-14 md:py-20 bg-warm-cream border-y border-stone/10" data-id="element-438">
        <div className="mx-auto max-w-7xl px-5" data-id="element-439">
          <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-440">
            <h2 className="text-2xl md:text-3xl font-heading text-navy mb-3" data-id="element-441">
              Why travellers choose our Ibiza airport transfer service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-442">
            {[{
            title: 'Fixed price before pickup',
            desc: 'No hidden fees at the end of the journey.'
          }, {
            title: 'Flight tracking included',
            desc: 'We monitor delays and adjust pickup time automatically.'
          }, {
            title: 'Meet & greet at arrivals',
            desc: 'Your driver is ready when you land.'
          }, {
            title: 'Local Ibiza drivers',
            desc: 'Drivers who know the island beyond GPS.'
          }, {
            title: 'Easy WhatsApp booking',
            desc: 'Fast quote, clear confirmation, no friction.'
          }].map((feature, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl p-5 shadow-sm" data-id="element-443">
                <CheckCircle2Icon className="w-5 h-5 text-gold-subtle mb-2.5" data-id="element-444" />
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-445">
                  {feature.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed" data-id="element-446">
                  {feature.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. FAQs */}
      <section className="py-16 md:py-24 bg-warm-white" data-id="element-447">
        <div className="mx-auto max-w-3xl px-5" data-id="element-448">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-449">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-450">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl overflow-hidden" data-id="element-451">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left" data-id="element-452">
                  <span className="font-semibold text-navy pr-4" data-id="element-453">{faq.q}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-454" />
                </button>
                <AnimatePresence data-id="element-455">
                  {openFaqIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-456">
                      <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate leading-relaxed" data-id="element-457">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>

          {/* FAQ closing CTA */}
          <div className="text-center mt-10" data-id="element-458">
            <p className="text-slate mb-4" data-id="element-459">
              Still unsure? Send us your flight number and destination on
              WhatsApp.
            </p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-460">
              <WhatsAppIcon className="w-5 h-5" data-id="element-461" />
              <span data-id="element-462">Get airport transfer price</span>
            </a>
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }} data-id="element-463" />
      </section>

      {/* 8. Reviews */}
      <section className="py-16 md:py-24 bg-white border-t border-stone/10" data-id="element-464">
        <div className="mx-auto max-w-7xl px-5" data-id="element-465">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-466">
            What clients say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-467">
            {[{
            text: 'In August our flight landed late, but the driver was already waiting. Quick, smooth and professional.',
            author: 'Airport pickup to San José — London'
          }, {
            text: 'Clear price, easy WhatsApp booking and no last-minute surprises.',
            author: 'Family arrival with return transfer — Madrid'
          }, {
            text: 'Fast response, great airport pickup and very easy communication.',
            author: 'Late arrival airport transfer — Paris'
          }].map((review, index) => <div key={index} className="bg-warm-cream p-8 rounded-2xl" data-id="element-468">
                <div className="flex space-x-1 mb-4" data-id="element-469">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-gold-subtle text-gold-subtle" data-id="element-470" />)}
                </div>
                <p className="text-navy font-medium mb-6 leading-relaxed" data-id="element-471">
                  "{review.text}"
                </p>
                <p className="text-sm text-slate" data-id="element-472">— {review.author}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. Internal Links */}
      <section className="py-14 md:py-20 bg-warm-white border-t border-stone/10" data-id="element-473">
        <div className="mx-auto max-w-4xl px-5" data-id="element-474">
          <p className="text-slate text-center mb-8" data-id="element-475">
            Need a different service? We also offer villa transfers, group
            transport and night driver service across Ibiza.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-476">
            <Link to="/ibiza-villa-transfer" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-477">
              <span className="font-medium text-navy text-sm" data-id="element-478">
                Ibiza Villa Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-479" />
            </Link>
            <Link to="/ibiza-group-transfer" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-480">
              <span className="font-medium text-navy text-sm" data-id="element-481">
                Ibiza Group Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-482" />
            </Link>
            <Link to="/ibiza-night-driver" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-483">
              <span className="font-medium text-navy text-sm" data-id="element-484">
                Ibiza Night Driver
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-485" />
            </Link>
          </div>
        </div>
      </section>

      {/* 10. Final CTA — compact */}
      <section className="py-14 md:py-20 bg-navy relative overflow-hidden" data-id="element-486">
        <div className="relative mx-auto max-w-3xl px-5 text-center z-10" data-id="element-487">
          <h2 className="text-3xl md:text-4xl font-heading text-white mb-4 leading-tight" data-id="element-488">
            Book your Ibiza airport transfer now
          </h2>
          <p className="text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-489">
            Send your flight number, destination and passenger count. We reply
            on WhatsApp in under 2 minutes with your fixed price.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 mb-4" data-id="element-490">
            <WhatsAppIcon className="w-5 h-5" data-id="element-491" />
            <span data-id="element-492">Get airport transfer price</span>
          </a>
          <p className="text-sm text-white/50" data-id="element-493">
            Instant reply · Fixed price · No upfront payment
          </p>
        </div>
      </section>
    </main>;
}